import React, { useState, useEffect } from 'react';
import { Table, Alert, Pagination, Spinner, Button } from 'react-bootstrap';
import { FaFileExcel } from 'react-icons/fa';
import moment from 'moment';

const ITENS_POR_PAGINA = 20;
const MENSAGEM_VAZIO = 'Nenhuma informação encontrada.';

// RN-03 — sigla de exibição da letra de status bruta (char(1)).
const SIGLA_STATUS = {
  D: 'DSP',
  A: 'ALG',
  B: 'BLQ',
  V: 'VEN',
  P: 'PVD',
  R: 'RSV',
  O: 'RAP',
};

const formatarSigla = (letra) => {
  if (!letra) return '';
  const chave = String(letra).trim().toUpperCase();
  return SIGLA_STATUS[chave] || chave;
};

const formatarData = (valor) => {
  if (!valor) return '';
  const m = moment(valor);
  return m.isValid() ? m.format('DD/MM/YYYY') : '';
};

// Formatação BR (#.##0,00).
const fmtValor = (valor) =>
  new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(valor || 0);

// Tx com 2 casas + '%'.
const fmtPercentual = (valor) =>
  `${new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(valor || 0)}%`;

const RenderizarPaginacao = ({ paginaAtual, totalPaginas, mudarPagina }) => {
  if (totalPaginas <= 1) return null;

  const itens = [];
  const maxBotoes = 5;

  itens.push(
    <Pagination.First
      key="first"
      onClick={() => mudarPagina(1)}
      disabled={paginaAtual === 1}
    />
  );
  itens.push(
    <Pagination.Prev
      key="prev"
      onClick={() => mudarPagina(paginaAtual - 1)}
      disabled={paginaAtual === 1}
    />
  );

  let inicioPaginas = Math.max(1, paginaAtual - Math.floor(maxBotoes / 2));
  const fimPaginas = Math.min(totalPaginas, inicioPaginas + maxBotoes - 1);

  if (fimPaginas - inicioPaginas < maxBotoes - 1) {
    inicioPaginas = Math.max(1, fimPaginas - maxBotoes + 1);
  }

  if (inicioPaginas > 1) {
    itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
  }

  for (let numero = inicioPaginas; numero <= fimPaginas; numero += 1) {
    itens.push(
      <Pagination.Item
        key={numero}
        active={numero === paginaAtual}
        onClick={() => mudarPagina(numero)}
      >
        {numero}
      </Pagination.Item>
    );
  }

  if (fimPaginas < totalPaginas) {
    itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
  }

  itens.push(
    <Pagination.Next
      key="next"
      onClick={() => mudarPagina(paginaAtual + 1)}
      disabled={paginaAtual === totalPaginas}
    />
  );
  itens.push(
    <Pagination.Last
      key="last"
      onClick={() => mudarPagina(totalPaginas)}
      disabled={paginaAtual === totalPaginas}
    />
  );

  return (
    <div className="d-flex justify-content-end align-items-center mb-2">
      <Pagination className="mb-0">{itens}</Pagination>
    </div>
  );
};

// Sub-tabela de correções/resíduo de um boleto (RN-07/expansão).
const LinhaCorrecoes = ({ correcoes, carregando, erro, colSpan }) => {
  return (
    <tr>
      <td colSpan={colSpan} className="bg-light">
        {carregando && (
          <span className="small text-muted">
            <Spinner animation="border" size="sm" className="mr-1" />
            Carregando histórico de correções...
          </span>
        )}
        {!carregando && erro && (
          <span className="small text-danger">{erro}</span>
        )}
        {!carregando && !erro && (!correcoes || correcoes.length === 0) && (
          <span className="small text-muted">
            Nenhuma correção encontrada para este boleto.
          </span>
        )}
        {!carregando && !erro && correcoes && correcoes.length > 0 && (
          <Table size="sm" bordered className="mb-0" style={{ fontSize: '0.8rem' }}>
            <thead>
              <tr>
                <th>Mês/Ano</th>
                <th className="text-right">Qtd</th>
                <th className="text-right">Valor</th>
                <th className="text-right">Tx (%)</th>
                <th className="text-right">Resíduo</th>
              </tr>
            </thead>
            <tbody>
              {correcoes.map((c, idx) => (
                <tr key={`corr-${idx}-${c.codigo_proposta}-${c.mes_ano}`}>
                  <td>{c.mes_ano}</td>
                  <td className="text-right">{c.qtd}</td>
                  <td className="text-right">{fmtValor(c.valor)}</td>
                  <td className="text-right">{fmtPercentual(c.tx)}</td>
                  <td className="text-right">{fmtValor(c.res)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </td>
    </tr>
  );
};

const TabelaHistoricoImoveis = ({
  itens,
  mostrarCorrecoes,
  onExpandirBoleto,
  onExportar,
}) => {
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [expandido, setExpandido] = useState({});
  const [correcoesPorBoleto, setCorrecoesPorBoleto] = useState({});

  const linhas = itens || [];

  useEffect(() => {
    setPaginaAtual(1);
    setExpandido({});
    setCorrecoesPorBoleto({});
  }, [itens]);

  if (linhas.length === 0) {
    return (
      <Alert variant="info" className="mt-3">
        {MENSAGEM_VAZIO}
      </Alert>
    );
  }

  const totalRegistros = linhas.length;
  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / ITENS_POR_PAGINA));
  const inicioSlice = (paginaAtual - 1) * ITENS_POR_PAGINA;
  const linhasPagina = linhas.slice(inicioSlice, inicioSlice + ITENS_POR_PAGINA);

  // Total geral dos valores de correção/resíduo exibidos (rodapé).
  const totalCorrecoes = Object.keys(correcoesPorBoleto).reduce((acc, chave) => {
    const reg = correcoesPorBoleto[chave];
    if (!reg || !reg.dados) return acc;
    return (
      acc +
      reg.dados.reduce((soma, c) => soma + (Number(c.res) || 0), 0)
    );
  }, 0);

  const COLUNAS = mostrarCorrecoes ? 8 : 7;

  const alternarExpansao = (boleto) => {
    if (!boleto) return;
    const chave = String(boleto);
    const jaExpandido = !!expandido[chave];
    setExpandido((prev) => ({ ...prev, [chave]: !jaExpandido }));

    // Carrega correções apenas na primeira expansão.
    if (!jaExpandido && !correcoesPorBoleto[chave]) {
      setCorrecoesPorBoleto((prev) => ({
        ...prev,
        [chave]: { carregando: true, erro: null, dados: null },
      }));
      onExpandirBoleto(boleto)
        .then((dados) => {
          setCorrecoesPorBoleto((prev) => ({
            ...prev,
            [chave]: { carregando: false, erro: null, dados: dados || [] },
          }));
        })
        .catch((err) => {
          const msg = err && err.message ? err.message : 'Erro ao carregar correções.';
          setCorrecoesPorBoleto((prev) => ({
            ...prev,
            [chave]: { carregando: false, erro: msg, dados: null },
          }));
        });
    }
  };

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Button variant="success" size="sm" onClick={onExportar}>
          <FaFileExcel className="mr-1" />
          Exportar Excel
        </Button>
        <RenderizarPaginacao
          paginaAtual={paginaAtual}
          totalPaginas={totalPaginas}
          mudarPagina={setPaginaAtual}
        />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <Table
          striped
          bordered
          hover
          size="sm"
          style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}
        >
          <thead className="thead-dark">
            <tr>
              {mostrarCorrecoes && <th style={{ minWidth: 40 }} />}
              <th style={{ minWidth: 220 }}>Família / Empreendimento</th>
              <th style={{ minWidth: 90 }}>Quadra</th>
              <th style={{ minWidth: 90 }}>Lote</th>
              <th className="text-center" style={{ minWidth: 70 }}>
                Status
              </th>
              <th style={{ minWidth: 100 }}>Data</th>
              <th style={{ minWidth: 200 }}>Cliente / Apelido</th>
              <th className="text-right" style={{ minWidth: 90 }}>
                Boleto
              </th>
            </tr>
          </thead>
          <tbody>
            {linhasPagina.map((item, idx) => {
              const posicaoAbsoluta = inicioSlice + idx;
              const chaveBoleto = item.boleto ? String(item.boleto) : null;
              const estaExpandido = chaveBoleto && !!expandido[chaveBoleto];
              const registroCorrecao = chaveBoleto
                ? correcoesPorBoleto[chaveBoleto]
                : null;
              // Expansão de correções só faz sentido em imóvel com negócio (tipo=0) e boleto.
              const podeExpandir =
                mostrarCorrecoes && item.tipo === 0 && !!item.boleto;

              return (
                <React.Fragment key={`hist-${posicaoAbsoluta}-${item.codigo_familia}`}>
                  <tr>
                    {mostrarCorrecoes && (
                      <td className="text-center">
                        {podeExpandir ? (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0"
                            onClick={() => alternarExpansao(item.boleto)}
                          >
                            {estaExpandido ? '−' : '+'}
                          </button>
                        ) : null}
                      </td>
                    )}
                    <td>
                      {item.codigo_familia} - {item.nome_da_familia || item.nome}
                    </td>
                    <td>{item.mapa1}</td>
                    <td>{item.mapa2}</td>
                    <td className="text-center">{formatarSigla(item.status)}</td>
                    <td>{formatarData(item.data)}</td>
                    <td>
                      {item.cli ? `${item.cli} - ` : ''}
                      {item.apelido_conta || ''}
                    </td>
                    <td className="text-right">{item.boleto || ''}</td>
                  </tr>
                  {mostrarCorrecoes && estaExpandido && registroCorrecao && (
                    <LinhaCorrecoes
                      colSpan={COLUNAS}
                      carregando={registroCorrecao.carregando}
                      erro={registroCorrecao.erro}
                      correcoes={registroCorrecao.dados}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          {mostrarCorrecoes && (
            <tfoot>
              <tr style={{ backgroundColor: '#eaf0fb' }}>
                <td colSpan={COLUNAS - 1} className="text-right">
                  <strong>Total Resíduo (correções exibidas)</strong>
                </td>
                <td className="text-right">
                  <strong>{fmtValor(totalCorrecoes)}</strong>
                </td>
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      <RenderizarPaginacao
        paginaAtual={paginaAtual}
        totalPaginas={totalPaginas}
        mudarPagina={setPaginaAtual}
      />
    </div>
  );
};

export default TabelaHistoricoImoveis;
